import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Repeat } from 'lucide-react';
import { healthChipClass } from '../utils/health';

/**
 * Card de proyecto en el grid de Metricas.
 * Estructura:
 *   - Header: dot color + nombre (sm bold) + chip health
 *   - Barra de progreso
 *   - Mini stats (X/Y tareas, Z vencidas, W vencen)
 *   - Linea IA (opcional, fondo cyan suave) cuando hay insight
 *
 * Similar a la card de ProyectosHomeView pero con health chip y linea IA.
 */
const ProyectoHealthCard = ({ proyecto, generandoInsight = false }) => {
  const {
    slug, nombre, color, es_continuo: esContinuo,
    stats, progreso_pct: pct,
    health,
    ia_insight: insight,
  } = proyecto;

  return (
    <Link
      to={`/proyectos/${slug}`}
      className="group block bg-white rounded-xl border border-slate-200/70 overflow-hidden transition-all duration-200 hover:border-[var(--proj-color)] hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.12)] hover:-translate-y-px"
      style={{ '--proj-color': color || '#06b6d4' }}
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-2.5 min-w-0">
            <span
              className="w-2.5 h-2.5 rounded-sm flex-shrink-0 mt-1"
              style={{ backgroundColor: color || '#06b6d4' }}
            />
            <div className="min-w-0">
              <h3 className="text-sm font-bold tracking-tight text-slate-900 truncate group-hover:text-slate-900">
                {nombre}
              </h3>
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-0.5">
                {slug}
              </p>
            </div>
          </div>
          {health && (
            <span
              className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold tracking-wide uppercase rounded border ${healthChipClass(health)} flex-shrink-0`}
            >
              {health.label}
            </span>
          )}
        </div>

        {/* Progreso (oculto para continuos) */}
        {!esContinuo && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-[10px] text-slate-500 tabular-nums mb-1.5">
              <span>{pct}% completado</span>
              <span>{stats.hechas}/{stats.total}</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ backgroundColor: color || '#06b6d4' }}
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              />
            </div>
          </div>
        )}

        {esContinuo && (
          <div className="flex items-center gap-1.5 mb-3 text-[11px] text-slate-500">
            <Repeat size={11} className="text-violet-500" strokeWidth={2} />
            <span>{stats.activas} {stats.activas === 1 ? 'tarea activa' : 'tareas activas'}</span>
          </div>
        )}

        {/* Mini stats */}
        <div className="flex items-center gap-3 text-[11px] text-slate-500 tabular-nums">
          {stats.vencidas > 0 && (
            <span className="text-rose-600 font-semibold">
              {stats.vencidas} {stats.vencidas === 1 ? 'vencida' : 'vencidas'}
            </span>
          )}
          {stats.vencen_periodo > 0 && (
            <span className="text-amber-600">
              {stats.vencen_periodo} {stats.vencen_periodo === 1 ? 'vence' : 'vencen'} este período
            </span>
          )}
          {stats.vencidas === 0 && stats.vencen_periodo === 0 && (
            <span className="text-slate-400">Sin vencimientos próximos</span>
          )}
        </div>
      </div>

      {/* Linea IA */}
      {(insight || generandoInsight) && (
        <div className="px-4 py-2.5 bg-cyan-50/60 border-t border-cyan-100/70 flex items-start gap-2">
          <Sparkles size={11} className="text-cyan-600 mt-0.5 flex-shrink-0" strokeWidth={2} />
          {insight && (
            <p className="text-[11.5px] text-slate-700 italic leading-snug">
              {insight.texto}
            </p>
          )}
          {generandoInsight && !insight && (
            <span className="text-[11px] text-slate-400 italic">Analizando...</span>
          )}
        </div>
      )}
    </Link>
  );
};

export default ProyectoHealthCard;
